import React, { useState, useEffect } from 'react';
import { BarChart3, FileText, CheckCircle, AlertCircle, TrendingUp } from 'lucide-react';
import { getDashboardData, DashboardData } from '../services/api';

interface DashboardPageProps {
  onViewProject: (projectId: string) => void;
}

const DashboardPage: React.FC<DashboardPageProps> = ({ onViewProject }) => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const dashboardData = await getDashboardData();
      setData(dashboardData);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Error loading dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="h-full w-full p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="h-full w-full p-6 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-900 mb-2">Error Loading Dashboard</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadDashboard}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const maxMethodologyCount = Math.max(...data.methodologyDistribution.map(m => m.count), 1);

  return (
    <div className="h-full w-full p-6 space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Overview</h2>
        <p className="text-gray-600">VWBA Projects dashboard and analytics</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">Total Projects</span>
            <FileText className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{data.totalProjects}</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">New Projects</span>
            <TrendingUp className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{data.newProjects}</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">Flagged</span>
            <AlertCircle className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{data.flaggedProjects}</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">Avg Data Completeness</span>
            <BarChart3 className="w-5 h-5 text-purple-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{data.avgDataCompleteness}%</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Methodology Distribution */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Methodology Distribution</h3>
          {data.methodologyDistribution.length === 0 ? (
            <p className="text-sm text-gray-500">No analyses completed yet</p>
          ) : (
            <div className="space-y-3">
              {data.methodologyDistribution.map((item) => (
                <div key={item.methodology} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 truncate max-w-[200px]">{item.methodology}</span>
                    <span className="font-medium text-gray-900">{item.count}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all"
                      style={{ width: `${(item.count / maxMethodologyCount) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Stage Gate Overview */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Stage Gate Overview</h3>
          {data.stageGateOverview.total === 0 ? (
            <p className="text-sm text-gray-500">No analyses completed yet</p>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">Stage 1 - Methodology Selection</span>
                  <span className="font-medium">
                    {data.stageGateOverview.stage1Approved}/{data.stageGateOverview.total}
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3">
                  <div
                    className="bg-green-500 h-3 rounded-full transition-all"
                    style={{
                      width: `${(data.stageGateOverview.stage1Approved / data.stageGateOverview.total) * 100}%`,
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Approved when methodology match score &ge; 70%
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">Stage 2 - Input Assessment</span>
                  <span className="font-medium">
                    {data.stageGateOverview.stage2Approved}/{data.stageGateOverview.total}
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3">
                  <div
                    className="bg-blue-500 h-3 rounded-full transition-all"
                    style={{
                      width: `${(data.stageGateOverview.stage2Approved / data.stageGateOverview.total) * 100}%`,
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Approved when data completeness &ge; 80%
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Projects */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Projects</h3>
        {data.recentProjects.length === 0 ? (
          <p className="text-sm text-gray-500">No projects yet</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {data.recentProjects.map((project) => (
              <div
                key={project.id}
                className="flex items-center justify-between py-3 cursor-pointer hover:bg-gray-50 px-2 rounded transition-colors"
                onClick={() => onViewProject(project.id)}
              >
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 p-1.5 rounded">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{project.name}</p>
                    <p className="text-xs text-gray-500">{formatDate(project.createdAt)}</p>
                  </div>
                </div>
                <CheckCircle className="w-4 h-4 text-green-500" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
